import {
    REF_SIZE, SIZE_OPTIONS, UNIT
} from '../../../constants/size';
import ObjectType from '../../../constants/ObjectType';
import { radians } from '../../../utils/degreesAndRadians';
import { linear, clamp01 } from '../../../utils/progress';

const THREE = require('three');

const setObjRotation = (obj3D, rotation) => {
    obj3D.rotation.set(
        radians(rotation.x ?? 0),
        radians(rotation.y ?? 0),
        radians(rotation.z ?? 0)
    );

    if (obj3D.hasReflection) {
        obj3D.reflection.rotation.set(
            radians(rotation.x ?? 0),
            radians(-1 * (rotation.y ?? 0)),
            radians(-1 * (rotation.z ?? 0))
        );
    }
};

class ObjectsHolder extends THREE.Group {
    constructor(data, webgl, stgs = null, factory = null/* , check */) {
        super();

        this._setup(data, stgs, webgl);
        this._startConstruct(data, webgl, stgs, factory);
    }

    async _startConstruct(data, webgl, stgs, factory) {
        await this._constructView(data, webgl, stgs, factory, this._orientation);
        this._handleReady();
    }

    _handleReady() {
        this._ready = true;

        if (this.hasTweens && this._started) {
            this._start();
        }
    }

    start() {
        if (this._started) return;

        if (this.hasTweens && this._ready) {
            this._start();
        }

        this._startObjects();

        this._started = true;
    }

    _setup(data, stgs, webgl) {
        this.data = data;
        this.name = data.name;
        this.webgl = webgl;

        // Set to true and override method resize to enable resize
        this.hasResize = false;

        const { clickable, renderOrder } = data;

        // Set to true and add method start to initialize user interaction
        this.clickable = clickable;
        this._layerChannel = clickable ? 1 : 0;

        // The flags to make SpaceView fire invalidate or set progress value for scrollable animation
        // Set to true and add method invalidate
        this.hasInvalidate = false;
        // Set to true and add method startObjects
        this.hasTweensObjects = false;
        // Set to true and add method start
        this.hasTweens = false;
        // Default state for hasProgressAnimation is false
        // 1. can be changed for child class
        this.hasProgressAnimation = false;
        // 2. changes to true if has any animation3d objects
        this.hasObjectsProgressAnimation = false;
        this._progress = 0;

        this._objectsZDefault = 0;

        // Set mainView reference to define the interaction hit area (object)
        this.mainView = null;

        this._started = false;
        this._objectsReady = false;
        this._ready = false;

        this.renderOrder = renderOrder;
    }

    _constructView(data, webgl, stgs, factory/* , orientation */) {
        const { objects, renderOrder } = data;

        if (objects && factory) this._addObjects(objects, webgl, factory, renderOrder || 0);
    }

    _addObjects(objects, webgl, factory, renderOrderIncrement = 0) {
        this.objectsGroup = null;
        this.objects3D = null;

        if (!objects || objects.length < 1) return;

        const animatedObjectsConfig = [];
        const objectsToCallInvalidate = [];
        const objectsToStartTween = [];
        const objectsConfigToResize = [];
        const objects3D = [];

        // Check flags according to the configs
        this.hasObjectsProgressAnimation = true;

        Promise.all(objects.map(async (obj) => {
            const {
                position, portrait, scale,
                rotation, z, renderOrder,
                onProgress
            } = obj;

            const { obj3D, reflect, reflection3D } = await factory.create(obj, webgl, factory);

            if (!obj3D) return;

            let wrapGroup = null;
            let wrapGroupRefl = null;

            if (onProgress) {
                const { from, to } = onProgress;

                animatedObjectsConfig.push({
                    obj3D, from: from ?? 0, to: to ?? 1, onProgress
                });

                wrapGroup = new THREE.Group();
                wrapGroupRefl = new THREE.Group();
            }

            if (obj.type === ObjectType.ANIMATION3D) {
                objectsToCallInvalidate.push(obj3D);
                if (obj3D.hasTweens) objectsToStartTween.push(obj3D);
            }

            if (position || rotation || portrait) {
                objectsConfigToResize.push({
                    obj3D: wrapGroup || obj3D,
                    resizeChildren: obj3D instanceof ObjectsHolder,
                    resizeTarget: obj3D,
                    position,
                    portrait,
                    rotation,
                    scale: scale ?? 1,
                    z: z ?? 0
                });
            } else if (typeof scale === 'number' && !Number.isNaN(scale)) {
                obj3D.scale.set(scale, scale, scale);
            }

            // console.log('RENDERORDER', this, (renderOrder || 0) + renderOrderIncrement, obj3D);

            if (typeof renderOrder === 'number' && !Number.isNaN(renderOrder)) {
                obj3D.renderOrder = (renderOrder || 0) + renderOrderIncrement;

                if (wrapGroup !== null) {
                    wrapGroup.renderOrder = obj3D.renderOrder;
                }
            }

            if (reflect && reflection3D) {
                obj3D.reflection = reflection3D;
                obj3D.hasReflection = true;
                reflection3D.renderOrder = obj3D.renderOrder;

                if (wrapGroup !== null) {
                    wrapGroup.reflection = wrapGroupRefl;
                    wrapGroup.hasReflection = true;
                }

                if (wrapGroupRefl !== null) {
                    wrapGroupRefl.renderOrder = obj3D.renderOrder;
                    wrapGroupRefl.add(reflection3D);
                    this.add(wrapGroupRefl);
                } else {
                    this.add(reflection3D);
                }
            }

            // console.log('ADD', obj3D);
            if (wrapGroup !== null) {
                wrapGroup.add(obj3D);
                this.add(wrapGroup);
            } else {
                this.add(obj3D);
            }

            objects3D.push(wrapGroup || obj3D);
        })).then(() => {
            if (animatedObjectsConfig.length > 0) this.hasObjectsProgressAnimation = true;
            if (objectsToCallInvalidate.length > 0) this.hasInvalidate = true;
            if (objectsToStartTween.length > 0) this.hasTweensObjects = true;
            if (objectsConfigToResize.length > 0) this.hasResize = true;

            this.objects3D = objects3D;
            this.animatedObjectsConfig = animatedObjectsConfig;
            this.objectsToCallInvalidate = objectsToCallInvalidate;
            this.objectsToStartTween = objectsToStartTween;
            this.objectsConfigToResize = objectsConfigToResize;

            this._handleObjectsReady();
        }).catch((e) => {
            console.warn(e);
        });
    }

    _handleObjectsReady() {
        this._objectsReady = true;
        this.resize(this._winWidth, this._winHeight, this._orientation, true, this._pixelRatio);
        this._updateObjectsAnimation(this.progress);

        if (this._started) {
            this._startObjects();
        }
    }

    _start() {
        console.warn(
            'Start tweens method _start must be overwritten for children classes before using.',
            '. Unit: ',
            this
        );
    }

    _startObjects() {
        if (!this.hasTweensObjects) return;

        this.objectsToStartTween.forEach((obj3D) => {
            if (typeof obj3D.start === 'function') obj3D.start();
        });
    }

    invalidate(t, dt) {
        if (!this.objectsToCallInvalidate) return;

        this.objectsToCallInvalidate.forEach((obj3D) => {
            if (typeof obj3D.invalidate === 'function') obj3D.invalidate(t, dt);
        });
    }

    _updateAnimation(value) {
        console.warn(
            'Animation update method _updateAnimation must be overwritten for children classes before using. Progress: ',
            value,
            '. Unit: ',
            this
        );
    }

    _updateObjectsAnimation(value) {
        if (!this._objectsReady) return;

        this.animatedObjectsConfig.forEach(({
            obj3D, from, to, onProgress
        }) => {
            const objProgress = clamp01(linear(value, from, to));
            const objCoordProgress = 0.5 - 0.5 * Math.cos(objProgress * Math.PI);

            // console.log(obj3D, value, objProgress);

            if (obj3D.progress === objProgress) return;

            const { position, rotation, opacity } = onProgress;

            if (rotation) {
                let { x, y, z } = rotation;

                if (typeof x === 'number' && !Number.isNaN(x)) x *= objCoordProgress;
                else x = 0;

                if (typeof y === 'number' && !Number.isNaN(y)) y *= objCoordProgress;
                else y = 0;

                if (typeof z === 'number' && !Number.isNaN(z)) z *= objCoordProgress;
                else z = 0;

                obj3D.rotation.set(
                    radians(x),
                    radians(y),
                    radians(z)
                );

                if (obj3D.hasReflection) {
                    obj3D.reflection.rotation.copy(obj3D.rotation);
                }
            }

            if (position) {
                let { x, y, z } = position;

                if (typeof x === 'number' && !Number.isNaN(x)) x *= objCoordProgress;
                else x = 0;

                if (typeof y === 'number' && !Number.isNaN(y)) y *= objCoordProgress;
                else y = 0;

                if (typeof z === 'number' && !Number.isNaN(z)) z *= objCoordProgress;
                else z = 0;

                obj3D.position.set(x, y, z);

                if (obj3D.hasReflection) {
                    obj3D.reflection.position.copy(obj3D.position);
                }
            }

            if (opacity) {
                const { start } = opacity;

                let opacityValue = objProgress;

                opacityValue = (typeof start !== 'number' || Number.isNaN(start)) ? opacityValue : (opacityValue - start) / (1 - start);

                if (opacityValue > 1) opacityValue = 1;
                if (opacityValue < 0) opacityValue = 0;

                if (opacity.direction < 0) {
                    opacityValue = 1 + opacity.direction * opacityValue;
                }
                if (opacity.speed) opacityValue *= opacity.speed;

                if (opacityValue > 1) opacityValue = 1;
                if (opacityValue < 0) opacityValue = 0;

                if (obj3D.material instanceof THREE.Material) obj3D.material.opacity = opacityValue;
            }

            obj3D.progress = objProgress;
        });
    }

    resize(winWidth, winHeight, orientation, force, pixelRatio) {
        this._resizeObjects(
            winWidth, winHeight, orientation, this._orientation !== orientation || force, force, pixelRatio
        );
        if (winWidth) this._winWidth = winWidth;
        if (winHeight) this._winHeight = winHeight;
        if (orientation) this._orientation = orientation;
        if (pixelRatio) this._pixelRatio = pixelRatio;
    }

    _resizeObjects(winWidth, winHeight, orientation, orientationChanged, force, pixelRatio) {
        if (!this.hasResize) return;

        // console.log('RESIZE OBJECTS', this.objectsConfigToResize, this);

        winWidth = winWidth ?? this._winWidth;
        winHeight = winHeight ?? this._winHeight;
        orientation = orientation ?? this.orientation;
        pixelRatio = pixelRatio ?? this._pixelRatio;

        // if (!winWidth) return;

        let grScale = 1;

        if (orientation === SIZE_OPTIONS.LANDSCAPE) {
            grScale = 1;
        } else {
            grScale = 1.8;
        }

        if (this._objectsReady) {
            this.objectsConfigToResize.forEach(({
                obj3D, position, rotation, scale, portrait, z, resizeChildren, resizeTarget
            }) => {
                if (resizeChildren) {
                    resizeTarget.resize(winWidth, winHeight, orientation, force, pixelRatio);
                }

                let newPosition2d = { x: 0, y: 0, ...position };

                if (orientation === SIZE_OPTIONS.PORTRAIT && portrait) {
                    if (portrait.position) {
                        newPosition2d = { ...newPosition2d, ...portrait.position };
                    }
                    if (typeof portrait.scale === 'number') {
                        scale = portrait.scale;
                    }
                }
                const newPosition = new THREE.Vector3(
                    newPosition2d.x,
                    newPosition2d.y,
                    z * UNIT.zSize - this.objectsZ
                );

                const w0 = REF_SIZE.w * UNIT.sizesScaleRatio;
                const w1of3 = winWidth / (3 * winHeight);
                const refW1of3 = REF_SIZE.w / (3 * REF_SIZE.h);

                if (Math.abs(newPosition.x) > w0 / 5) {
                    if (newPosition.x > 0) newPosition.x = w0 / 5 + ((newPosition.x - w0 / 5) * w1of3) / refW1of3;
                    else newPosition.x = -w0 / 5 + ((newPosition.x + w0 / 5) * w1of3) / refW1of3;
                }

                newPosition.x *= grScale;
                newPosition.y *= grScale;

                obj3D.position.copy(newPosition);
                obj3D.scale.set(grScale * scale, grScale * scale, grScale * scale);
                // console.log('RESIZE', newPosition, scale, obj3D);

                if (orientationChanged) {
                    let newRotation2d = { ...rotation };

                    if (orientation === SIZE_OPTIONS.PORTRAIT && portrait && portrait.rotation) {
                        newRotation2d = { ...newRotation2d, ...portrait.rotation };
                    }

                    setObjRotation(obj3D, newRotation2d);
                }

                if (obj3D.hasReflection) {
                    newPosition.x *= -1;

                    const { reflection } = obj3D;
                    const objScale = obj3D.scale;

                    reflection.position.copy(newPosition);
                    reflection.scale.set(-objScale.x, objScale.y, objScale.z);
                    reflection.renderOrder = obj3D.renderOrder;
                }
            });
        }
    }

    set progress(value) {
        if (this._progress === value) return;

        if (this.hasProgressAnimation) this._updateAnimation(value);
        if (this.hasObjectsProgressAnimation) this._updateObjectsAnimation(value);

        this._progress = value;
    }

    get progress() {
        return this._progress;
    }

    get objectsZ() {
        return this._objectsZDefault;
    }
}

export default ObjectsHolder;
